import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import {
  Alert,
  alertTypes,
  alertVariants,
} from "@visiblethread/react-components/dist/components/Alert";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { isNameValid } from "utils";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { useDispatch } from "react-redux";

const TextFieldModal = ({
  inputText,
  subHeading,
  title,
  placeholderText,
  buttonText,
  errorMessage,
  onOk,
  selectTextOnInitialLoad,
}) => {
  const dispatch = useDispatch();
  const [text, setText] = useState(inputText || "");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleClose = () => {
    dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
  };

  const isDisabled = () => text.trim() === "" || error !== "";

  const onSubmit = () => {
    if (!isDisabled()) {
      onOk(text.trim());
    }
  };

  const onEnterKeyDown = ({ key }) => {
    if (key === "Enter") {
      onSubmit();
    }
  };

  useEffect(() => {
    if (text.trim().length && !isNameValid(text)) {
      setError(errorMessage);
    } else {
      setError("");
    }
  }, [text]);

  useEffect(() => {
    const input = inputRef.current;
    if (selectTextOnInitialLoad && input) {
      const textLength = input.value.length;
      input.focus();
      input.setSelectionRange(0, textLength);
    }
  }, []);

  return (
    <Modal
      data-testid="text-field-modal"
      className="fade-in"
      show
      onDismiss={handleClose}
      backdrop="static"
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header closeButton>
        <b>{title}</b>
      </Modal.Header>
      <Modal.Body>
        {error !== "" ? (
          <Alert
            variant={alertVariants.warning}
            text={error}
            type={alertTypes.inline}
            hideCloseButton
          />
        ) : null}
        {subHeading ? (
          <div className="text_field_modal__sub_heading">{subHeading}</div>
        ) : null}
        <input
          type="text"
          required
          placeholder={placeholderText}
          value={text}
          onChange={(e) => setText(e.target.value)}
          ref={inputRef}
          className="form__field_text"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={buttonVariants.primary}
          content={buttonText || "Ok"}
          onClick={onSubmit}
          disabled={isDisabled()}
          dataTestId="text-field-modal-button"
        />
        <Button
          dataTestId="cancelButton"
          variant={buttonVariants.secondary}
          onClick={handleClose}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default TextFieldModal;

TextFieldModal.propTypes = {
  inputText: PropTypes.string,
  subHeading: PropTypes.string,
  title: PropTypes.string.isRequired,
  placeholderText: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  errorMessage: PropTypes.string.isRequired,
  onOk: PropTypes.func.isRequired,
  selectTextOnInitialLoad: PropTypes.bool,
};
